import type { DictFragment } from '../translations';

/** Project side-panel & editor strings: meetings, documents, milestones,
 * deliverables editor, change requests, attachments, transcript. */
export const projectExtras: DictFragment = {
  fr: {
    // meetings.
    'meetings.title': 'Réunions ({n})',
    'meetings.addMeeting': 'Ajouter une réunion',
    'meetings.empty': 'Aucune réunion liée à ce projet.',
    'meetings.upcoming': 'À venir',
    'meetings.past': 'Passées',
    'meetings.cancelled': 'Annulée',
    'meetings.fuzzy': 'approximatif',
    'meetings.join': 'Rejoindre',
    'meetings.transcript': 'Transcription',
    'meetings.transcriptComingSoon': 'Transcription (bientôt disponible)',
    'meetings.sourceManual': 'Manuel · {name}',
    'meetings.unknown': 'Inconnu',
    'meetings.deleteMeeting': 'Supprimer la réunion',
    'meetings.showLess': 'Afficher moins',
    'meetings.showMore': '+{n} de plus',
    'meetings.subjectPlaceholder': 'Sujet de la réunion',
    'meetings.locationPlaceholder': 'Lieu (facultatif)',
    'meetings.attendeesPlaceholder': 'Participants (noms séparés par des virgules)',
    'meetings.createFailed': 'Échec de la création de la réunion',
    'meetings.deleteFailed': 'Échec de la suppression de la réunion',

    // documents.
    'documents.title': 'Documents',
    'documents.empty': 'Aucun document pour le moment.',

    // milestones.
    'milestones.empty': 'Aucun jalon pour le moment.',
    'milestones.addMilestone': 'Ajouter un jalon',
    'milestones.milestoneN': 'Jalon {n}',
    'milestones.dragReorder': 'Glisser pour réordonner',
    'milestones.removeMilestone': 'Retirer le jalon',
    'milestones.removeConfirmTitle': 'Supprimer le jalon ?',
    'milestones.undoCancel': 'Rétablir',
    'milestones.removeConfirmNew': 'Êtes-vous certain·e de vouloir supprimer le jalon ?',
    'milestones.cancelOnSave': 'Sera annulé à l’enregistrement — il restera ici, grisé, et la trace sera notée dans la chronologie et les commentaires.',
    'milestones.removeConfirm': 'Êtes-vous certain·e de vouloir supprimer le jalon ? Il restera visible à sa place, grisé et marqué « Annulé ».',
    'milestones.removeConfirmTasks': 'Êtes-vous certain·e de vouloir supprimer le jalon ? {n} tâche(s) ouverte(s) sont encore liées à ce jalon : elles passeront dans « Archivées » à l’enregistrement. Le jalon restera visible à sa place, grisé et marqué « Annulé ».',
    'milestones.titlePlaceholder': 'Titre du jalon',
    'milestones.descriptionPlaceholder': 'Ce que ce jalon livre',
    'milestones.estimatedStart': 'Début de la mise en œuvre du jalon',
    'milestones.estimatedEnd': 'Fin estimée du jalon',
    'milestones.dueDateHistoryToggle': 'Historique ({n})',
    'milestones.dueDateHistoryEntry': '{old} → {new} — modifié le {date} par {who}',
    'milestones.dueDateHistoryNone': 'aucune date',
    'milestones.whoDoesWhat': 'Qui fait quoi',
    'milestones.assignPlaceholder': 'Ce que {name} fait dans ce jalon',
    'milestones.unassign': 'Désassigner',
    'milestones.assign': 'Assigner :',
    'milestones.noneAssigned': 'Personne d’assigné.',
    'milestones.acceptance': 'Critères d’acceptation',
    'milestones.acceptancePlaceholder': 'Comment nous saurons que ce jalon est terminé',
    'milestones.linkedDeliverables': 'Livrables liés (facultatif) :',
    'milestones.untitled': 'Sans titre',

    // deliverablesEditor.
    'deliverablesEditor.empty': 'Aucun livrable pour le moment.',
    'deliverablesEditor.noneDefined': 'Aucun livrable défini.',
    'deliverablesEditor.add': 'Ajouter un livrable',
    'deliverablesEditor.titlePlaceholder': 'Titre du livrable',
    'deliverablesEditor.bodyPlaceholder': 'Décrire ce livrable…',
    'deliverablesEditor.expand': 'Développer',
    'deliverablesEditor.collapse': 'Réduire',
    'deliverablesEditor.untitled': 'Livrable sans titre',

    // changeReq.
    'changeReq.projectFallback': 'Projet',
    'changeReq.requestedByLabel': 'Demandé par',
    'changeReq.stalenessWarning': 'Ce projet a été modifié depuis la demande de modification. L’approbation appliquera ces changements par-dessus l’état actuel.',
    'changeReq.noDifferences': 'Aucune différence détectée entre l’état actuel et les modifications proposées.',
    'changeReq.current': 'Actuel',
    'changeReq.proposed': 'Proposé',
    'changeReq.commentPlaceholder': 'Ajouter un commentaire (facultatif)…',
    'changeReq.reject': 'Rejeter',
    'changeReq.approve': 'Approuver',
    'changeReq.forceApprove': 'Forcer l’approbation',
    'changeReq.emptyValue': 'Vide',
    'changeReq.field.name': 'Nom de l’initiative',
    'changeReq.field.problem': 'Problème',
    'changeReq.field.goal': 'Objectif',
    'changeReq.field.valueAdded': 'Valeur ajoutée',
    'changeReq.field.deliverables': 'Livrables',
    'changeReq.field.resourcesEffort': 'Ressources et effort',
    'changeReq.field.ownership': 'Responsabilité',
    'changeReq.field.dependencies': 'Dépendances',
    'changeReq.field.domain': 'Domaine du portefeuille',
    'changeReq.field.level': 'Niveau stratégique',
    'changeReq.field.status': 'Statut',
    'changeReq.field.prequalification': 'Préqualification',
    'changeReq.field.scoping': 'Cadrage',
    'changeReq.field.planning': 'Planification',
    'changeReq.field.execution': 'Exécution',
    'changeReq.field.handover': 'Transfert',
    'changeReq.field.deferredReview': 'Revue reportée',
    'changeReq.field.sequencing': 'Séquencement',

    // attachments.
    'attachments.attachFiles': 'Joindre des fichiers',
    'attachments.deleteAttachment': 'Supprimer la pièce jointe',

    // transcript.
    'transcript.loadFailed': 'Échec du chargement de la transcription.',
    'transcript.tabTranscript': 'Transcription',
    'transcript.tabSummary': 'Résumé',
    'transcript.noContent': 'Aucun contenu de transcription disponible.',
    'transcript.voice': 'Voix {n}',
    'transcript.speakerEdit': 'Nommer cette voix',
    'transcript.speakerAuto': 'reconnu·e à {pct} %',
    'transcript.speakerClosest': 'Voix proches',
    'transcript.speakerPlaceholder': 'Nom de la personne',
    'transcript.speakerClear': 'Retirer le nom',
    'transcript.speakerHint': 'Le nom part aussi dans Speakr, qui retient cette voix pour la suite.',
    'transcript.speakerFailed': 'Le nom n’a pas pu être enregistré : {error}',
    'transcript.sharedWith': 'Partagé dans Speakr avec {names}',
    'transcript.sharedSimulated': '(simulé : sur dev, rien n’est partagé)',
    'transcript.redo': 'Refaire la transcription',
    'transcript.redoRetry': 'Relancer',
    'transcript.redoHelp': 'Speakr refait la transcription à partir de l’enregistrement, en quelques minutes. Elle remplace l’actuelle, et les personnes sont reconnues à nouveau d’après leur voix.',
    'transcript.redoSpeakers': 'Nombre de personnes qui parlent',
    'transcript.redoSpeakersAuto': 'auto',
    'transcript.redoSpeakersHint': 'Facultatif : aide Speakr à ne pas confondre ni dédoubler les voix.',
    'transcript.redoGo': 'Refaire',
    'transcript.redoSending': 'Envoi…',
    'transcript.redoCancel': 'Annuler',
    'transcript.redoFailed': 'La transcription n’a pas pu être relancée : {error}',
    'transcript.redoBusy': 'Speakr travaille encore sur cet enregistrement (transcription ou résumé) : réessayez dans quelques minutes.',
    'transcript.redoNoAudio': 'Speakr n’a plus l’audio de cet enregistrement : il ne peut pas le transcrire à nouveau.',
    'transcript.redoNoAccount': 'Le compte Speakr de la personne qui a ajouté l’enregistrement n’est pas relié à PMO.',
  },
  en: {
    // meetings.
    'meetings.title': 'Meetings ({n})',
    'meetings.addMeeting': 'Add meeting',
    'meetings.empty': 'No meetings linked to this project.',
    'meetings.upcoming': 'Upcoming',
    'meetings.past': 'Past',
    'meetings.cancelled': 'Cancelled',
    'meetings.fuzzy': 'fuzzy',
    'meetings.join': 'Join',
    'meetings.transcript': 'Transcript',
    'meetings.transcriptComingSoon': 'Transcript (coming soon)',
    'meetings.sourceManual': 'Manual · {name}',
    'meetings.unknown': 'Unknown',
    'meetings.deleteMeeting': 'Delete meeting',
    'meetings.showLess': 'Show less',
    'meetings.showMore': '+{n} more',
    'meetings.subjectPlaceholder': 'Meeting subject',
    'meetings.locationPlaceholder': 'Location (optional)',
    'meetings.attendeesPlaceholder': 'Attendees (comma-separated names)',
    'meetings.createFailed': 'Failed to create meeting',
    'meetings.deleteFailed': 'Failed to delete meeting',

    // documents.
    'documents.title': 'Documents',
    'documents.empty': 'No documents yet.',

    // milestones.
    'milestones.empty': 'No milestones yet.',
    'milestones.addMilestone': 'Add milestone',
    'milestones.milestoneN': 'Milestone {n}',
    'milestones.dragReorder': 'Drag to reorder',
    'milestones.removeMilestone': 'Remove milestone',
    'milestones.removeConfirmTitle': 'Delete the milestone?',
    'milestones.undoCancel': 'Undo',
    'milestones.removeConfirmNew': 'Are you sure you want to delete this milestone?',
    'milestones.cancelOnSave': 'Will be cancelled on save — it stays here, greyed, and the trace is noted in the timeline and comments.',
    'milestones.removeConfirm': 'Are you sure you want to delete this milestone? It stays visible in its place, greyed and marked “Cancelled”.',
    'milestones.removeConfirmTasks': 'Are you sure you want to delete this milestone? {n} open task(s) are still linked to it: they will move to “Archived” on save. The milestone stays visible in its place, greyed and marked “Cancelled”.',
    'milestones.titlePlaceholder': 'Milestone title',
    'milestones.descriptionPlaceholder': 'What this milestone delivers',
    'milestones.estimatedStart': 'Start of milestone implementation',
    'milestones.estimatedEnd': 'Estimated end of milestone',
    'milestones.dueDateHistoryToggle': 'History ({n})',
    'milestones.dueDateHistoryEntry': '{old} → {new} — changed on {date} by {who}',
    'milestones.dueDateHistoryNone': 'no date',
    'milestones.whoDoesWhat': 'Who does what',
    'milestones.assignPlaceholder': 'What {name} does in this milestone',
    'milestones.unassign': 'Unassign',
    'milestones.assign': 'Assign:',
    'milestones.noneAssigned': 'No one assigned.',
    'milestones.acceptance': 'Acceptance criteria',
    'milestones.acceptancePlaceholder': "How we'll know this milestone is complete",
    'milestones.linkedDeliverables': 'Linked deliverables (optional):',
    'milestones.untitled': 'Untitled',

    // deliverablesEditor.
    'deliverablesEditor.empty': 'No deliverables yet.',
    'deliverablesEditor.noneDefined': 'No deliverables defined.',
    'deliverablesEditor.add': 'Add deliverable',
    'deliverablesEditor.titlePlaceholder': 'Deliverable title',
    'deliverablesEditor.bodyPlaceholder': 'Describe this deliverable…',
    'deliverablesEditor.expand': 'Expand',
    'deliverablesEditor.collapse': 'Collapse',
    'deliverablesEditor.untitled': 'Untitled deliverable',

    // changeReq.
    'changeReq.projectFallback': 'Project',
    'changeReq.requestedByLabel': 'Requested by',
    'changeReq.stalenessWarning': 'This project has been modified since this change was requested. Approving will apply these changes on top of the current state.',
    'changeReq.noDifferences': 'No differences detected between current state and proposed changes.',
    'changeReq.current': 'Current',
    'changeReq.proposed': 'Proposed',
    'changeReq.commentPlaceholder': 'Add a comment (optional)...',
    'changeReq.reject': 'Reject',
    'changeReq.approve': 'Approve',
    'changeReq.forceApprove': 'Force Approve',
    'changeReq.emptyValue': 'Empty',
    'changeReq.field.name': 'Initiative Name',
    'changeReq.field.problem': 'Problem',
    'changeReq.field.goal': 'Goal',
    'changeReq.field.valueAdded': 'Value Added',
    'changeReq.field.deliverables': 'Deliverables',
    'changeReq.field.resourcesEffort': 'Resources & Effort',
    'changeReq.field.ownership': 'Ownership',
    'changeReq.field.dependencies': 'Dependencies',
    'changeReq.field.domain': 'Portfolio Domain',
    'changeReq.field.level': 'Strategic Level',
    'changeReq.field.status': 'Status',
    'changeReq.field.prequalification': 'Préqualification',
    'changeReq.field.scoping': 'Scoping',
    'changeReq.field.planning': 'Planning',
    'changeReq.field.execution': 'Execution',
    'changeReq.field.handover': 'Handover',
    'changeReq.field.deferredReview': 'Deferred Review',
    'changeReq.field.sequencing': 'Sequencing',

    // attachments.
    'attachments.attachFiles': 'Attach files',
    'attachments.deleteAttachment': 'Delete attachment',

    // transcript.
    'transcript.loadFailed': 'Failed to load transcript.',
    'transcript.tabTranscript': 'Transcript',
    'transcript.tabSummary': 'Summary',
    'transcript.noContent': 'No transcript content available.',
    'transcript.voice': 'Voice {n}',
    'transcript.speakerEdit': 'Name this voice',
    'transcript.speakerAuto': 'recognised at {pct}%',
    'transcript.speakerClosest': 'Closest voices',
    'transcript.speakerPlaceholder': 'Person’s name',
    'transcript.speakerClear': 'Remove the name',
    'transcript.speakerHint': 'The name goes to Speakr too, which remembers this voice from now on.',
    'transcript.speakerFailed': 'The name could not be saved: {error}',
    'transcript.sharedWith': 'Shared in Speakr with {names}',
    'transcript.sharedSimulated': '(simulated: nothing is shared on dev)',
    'transcript.redo': 'Transcribe again',
    'transcript.redoRetry': 'Try again',
    'transcript.redoHelp': 'Speakr transcribes the recording again from its audio, in a few minutes. The new transcript replaces this one, and people are recognised again from their voices.',
    'transcript.redoSpeakers': 'Number of people speaking',
    'transcript.redoSpeakersAuto': 'auto',
    'transcript.redoSpeakersHint': 'Optional: helps Speakr not to mix up or split voices.',
    'transcript.redoGo': 'Transcribe again',
    'transcript.redoSending': 'Sending…',
    'transcript.redoCancel': 'Cancel',
    'transcript.redoFailed': 'The transcription could not be started again: {error}',
    'transcript.redoBusy': 'Speakr is still working on this recording (transcript or summary): try again in a few minutes.',
    'transcript.redoNoAudio': 'Speakr no longer has this recording’s audio, so it cannot transcribe it again.',
    'transcript.redoNoAccount': 'The Speakr account of the person who added the recording is not connected to PMO.',
  },
};
